import { defineNuxtPlugin, useRouter, useRuntimeConfig } from "nuxt/app";
import { Analytics } from "../service/analytics";
import { GoogleTagManager } from "../service/providers/google-tag-manager";
import { MixPanel } from "../service/providers/mixpanel";
import { Snowplow } from "../service/providers/snowplow";

export default defineNuxtPlugin(() => {
  const router = useRouter();
  const runtimeConfig = useRuntimeConfig();

  // Lazily instantiate Analytics to avoid accessing Pinia before it is ready.
  // The @pinia/nuxt plugin clears the active Pinia after app:rendered, so any
  // top-level Pinia access inside a Nuxt plugin can race with that teardown.
  let _analytics: Analytics | undefined;
  const getAnalytics = (): Analytics => {
    if (!_analytics) {
      _analytics = new Analytics();
    }
    return _analytics;
  };

  router.isReady?.().then(() => {
    router.afterEach((to, from) => {
      if (!import.meta.browser) return;
      getAnalytics().trackEvent({
        name: "pageView",
        payload: {
          originRoutePath: from.fullPath,
          originRouteName: from.name?.toString(),
          targetRoutePath: to.fullPath,
          targetRouteName: to.name?.toString(),
        },
      });
    });
  });

  return {
    provide: {
      analytics: {
        trackEvent: (event: Parameters<Analytics["trackEvent"]>[0]) => {
          if (!import.meta.browser) return;
          getAnalytics().trackEvent(event);
        },
        releaseQueue: () => {
          if (!import.meta.browser) return;
          getAnalytics().releaseQueue();
        },
        setSuperProperties: (
          properties: Parameters<Analytics["setSuperProperties"]>[0],
        ) => {
          if (!import.meta.browser) return;
          getAnalytics().setSuperProperties(properties);
        },
        identify: (user: Parameters<Analytics["identify"]>[0]) => {
          if (!import.meta.browser) return;
          getAnalytics().identify(user);
        },
        anonymize: () => {
          if (!import.meta.browser) return;
          getAnalytics().anonymize();
        },

        enableSnowplow: () => {
          if (!runtimeConfig.public.analytics.snowplow.collector.length) return;

          getAnalytics().addProvider(
            new Snowplow({
              collector: runtimeConfig.public.analytics.snowplow.collector,
              postPath: runtimeConfig.public.analytics.snowplow.postPath,
              appId: runtimeConfig.public.analytics.snowplow.appId,
              appVersion: runtimeConfig.public.analytics.snowplow.appVersion,
              schemaVendor:
                runtimeConfig.public.analytics.snowplow.schemaVendor,
            }),
          );
        },

        enableMixPanel: () => {
          if (!runtimeConfig.public.analytics.mixpanel.token.length) return;

          getAnalytics().addProvider(
            new MixPanel({
              token: runtimeConfig.public.analytics.mixpanel.token,
              debug: runtimeConfig.public.analytics.mixpanel.debug,
            }),
          );
        },

        enableGoogleAnalytics: (
          config: ConstructorParameters<typeof GoogleTagManager>[0],
        ) => {
          getAnalytics().addProvider(new GoogleTagManager(config));
        },
      },
    },
  };
});
